import type { AudienceKind, ListingState } from '@ralph/contracts'
import type { NeedsOwnerRow } from '@/api/maker.ts'
import type { Counting } from './systems.ts'

/**
 * `admin.capabilities` — the two curation backlogs an administrator works.
 *
 * ── The capability wording queue (PRD-18 AGM-080's phase-4 half) ──
 *
 * The sharpest line here is the one about the claimed title and description: they arrive
 * from the connected system, which is not part of this platform's trust boundary, and a
 * capability that describes itself as "reads your calendar" may do considerably more. The
 * queue therefore presents them as drafting material to check, never as the description —
 * an administrator who copies them verbatim has published someone else's words as the
 * platform's own, which is exactly what curation exists to prevent.
 *
 * Until a capability is curated it is invisible to makers. That is stated on the panel,
 * because "why can't I see this connection in the builder" is otherwise an unanswerable
 * question for the maker and an invisible backlog for the administrator.
 *
 * ── `listings`: the words PRD-26 added to the studio's Curation tab ──
 *
 * The listing controls keep their catalogue in the maker namespace; what is here is the part
 * the groups work reshaped — the "Needs an owner" queue, which now holds knowledge bases as
 * well as agents and takes a person or a group as the new owner, and the audience a listing
 * row names. It sits under this namespace rather than its own because both halves are the
 * same job: an administrator deciding how something other people wrote is described.
 *
 * `NAME_MAX` and `DESCRIPTION_MAX` are not here. They are validation limits and live in
 * `components/marketplace/admin/capability-limits.ts` (PRD-27 F-20, D-25): a limit that can
 * differ per locale refuses one reader's input and accepts another's.
 */

export const capabilities = {
  title: 'Capability wording',
  lead: 'Connected capabilities are hidden from makers until an administrator writes what they do in plain language. This is that backlog.',
  loading: 'Loading the queue…',
  unavailable: 'The curation queue is not available on this deployment yet',
  unavailableBody: 'GET /api/admin/mcp/curation-queue has not answered. Existing curated wording is unaffected.',
  empty: 'Nothing waiting',
  emptyBody: 'Every connected capability on this deployment has curated wording.',

  claimedTitle: 'What the connected system claims',
  claimedHint: 'Written by that system, not by anyone here. Check it against what the capability actually does.',
  useAsDraft: 'Use as a starting point',
  riskLabel: 'risk',
  curatedBadge: 'curated',
  uncuratedBadge: 'hidden from makers',

  nameLabel: 'Name makers and people will see',
  namePlaceholder: 'Create a support ticket',
  descriptionLabel: 'What it does, in one or two sentences',
  descriptionPlaceholder: 'Opens a ticket in the support system on the person’s behalf. It cannot close or edit one.',
  wordingLabel: 'Does it read, or does it act?',
  wordingReads: 'Reads information',
  wordingActs: 'Takes an action — the person is always asked first',
  wordingHint:
    'This decides which disclosure a person sees before their first chat. Anything that changes something outside this platform acts.',

  save: 'Save wording',
  saving: 'Saving…',
  saved: 'Saved — makers can now see this capability.',
  saveFailed: 'The wording could not be saved.',
  withdraw: 'Withdraw wording',
  withdrawing: 'Withdrawing…',
  withdrawHint: 'Withdrawing hides it from makers again. Agents that already use it are unaffected.',
  withdrawFailed: 'The wording could not be withdrawn.',

  /**
   * The characters left in a capped field. English needs one form and Portuguese needs two
   * — *resta um* against *restam doze* — so both locales ask `Intl.PluralRules` rather than
   * the catalogue assuming the shape of the language it was written in.
   */
  nameCount: (fmt: Counting, left: number): string => `${fmt.formatCount(left)} left`,
  descriptionCount: (fmt: Counting, left: number): string => `${fmt.formatCount(left)} left`,

  listings: {
    /**
     * `satisfies` rather than `as Record<string, string>`, which is not a typing preference: the
     * cast made every one of these maps indexable by anything, so the call site needed a
     * `?? listing.audienceKind` to cover a miss — and that fallback put the wire identifier on
     * screen. Typed against the contract enum, a miss is impossible and a new member is a
     * compile error here (PRD-27 D-9).
     */
    audience: {
      private: 'only its maintainers',
      groups: 'shared with groups',
      everyone: 'everyone'
    } satisfies Record<AudienceKind, string>,

    /**
     * A listing's state, in words.
     *
     * The row rendered `listing.state` raw — "published", "suspended" — which is the wire value
     * and not a word anybody chose to show. Kept local for now; two other copies of these four
     * labels already exist — the maker namespace's `lifecycle.state` and the gallery's `state` —
     * and collapsing all three is a later phase's job, not this one's.
     */
    state: {
      draft: 'Draft',
      published: 'Published',
      archived: 'Retired',
      suspended: 'Paused by an administrator'
    } satisfies Record<ListingState, string>,

    needsOwner: {
      hint: 'Moving something never changes who can use it. An Official badge comes off with a move until an administrator grants it again.',
      loading: 'Loading…',
      failed: 'This list could not be loaded. Nothing has been changed.',
      empty: 'Everything has an owner who can maintain it.',
      /** Guarded against the row's own union, so a third ownable kind is a compile error here. */
      kind: { agent: 'Agent', knowledge_base: 'Knowledge base' } satisfies Record<NeedsOwnerRow['kind'], string>,
      ownedBy: 'Owned by',
      ownerLeft: (name: string): string => `Owned by ${name}, whose account is deactivated`,
      groupUnmanaged: 'which has no managers',
      /**
       * Wraps `formatAgo`, so it is a composed sentence and has to be read as one in both
       * languages (LOC-022). English adds "since" in front of "5m ago"; Portuguese must not,
       * because `RelativeTimeFormat` already carries its own preposition there.
       */
      since: (when: string): string => `since ${when}`,
      choose: 'Choose a new owner',
      chooseLabel: (name: string): string => `Choose a new owner for ${name}`,
      move: 'Move',
      moving: 'Moving…',
      cancel: 'Cancel',
      notMoved: 'It was not moved.',
      moved: (name: string, owner: string): string => `${name} now belongs to ${owner}.`
    }
  }
}
